// src/app/routes.jsx
import React, { Suspense, lazy } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  Outlet,
  useLocation,
} from "react-router-dom";
import ProtectedRoute from "../core/auth/ProtectedRoute";
import PublicRoute from "../core/auth/PublicRoute";
import ErrorBoundary from "../shared/components/ErrorBoundary";
import EmailVerificationBanner from "../core/auth/EmailVerificationBanner";
import StripeConnectBanner from "../features/teachers/components/StripeConnectBanner";
import AchievementNotifications from "../features/achievements/components/AchievementNotifications";
import { PageLoadingFallback } from "../shared/components/LoadingFallback";
import AppLayout from "../shared/components/AppLayout";
import StripeConnectGate from "../features/teachers/components/StripeConnectGate";

const COMING_SOON_MODE = import.meta.env.VITE_COMING_SOON_MODE === "true";

// Lazy load pages for better performance and code splitting
const LandingPage = lazy(() => import("../features/landing/pages"));
const ComingSoonPage = lazy(() => import("../features/landing/pages/ComingSoonPage"));

const Dashboard = lazy(() => import("../features/dashboard/pages/Dashboard"));
const AdminDashboard = lazy(() => import("../features/admin/pages/AdminDashboard/AdminDashboardSimplified"));
const TeacherDashboard = lazy(() => import("../features/teachers/pages/TeacherDashboard"));

const CourseCreatePage = lazy(() => import("../features/courses/pages/CourseCreationPage"));
const LearnPage = lazy(() => import("../features/courses/pages/LearnPage"));
const CourseDetailPage = lazy(() => import("../features/courses/pages/CourseDetailPage"));
const TutorialPage = lazy(() => import("../features/tutorials/pages/TutorialPage"));

const AccountPage = lazy(() => import("../features/account/pages/AccountPage"));
const FeedPage = lazy(() => import("../features/feed/pages/FeedPage"));
const UserProfilePage = lazy(() => import("../features/feed/pages/UserProfilePage"));

const MultitracksPage = lazy(() => import("../features/multitracks/pages/MultitracksPage"));
const MultitrackDetailPage = lazy(() => import("../features/multitracks/pages/MultitrackDetailPage"));

const LoginPage = lazy(() => import("../core/auth/pages/LoginPage"));
const SignupPage = lazy(() => import("../core/auth/pages/SignupPage"));
const ForgotPasswordPage = lazy(() => import("../core/auth/pages/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("../core/auth/pages/ResetPasswordPage"));
const VerifyEmailPage = lazy(() => import("../core/auth/pages/VerifyEmailPage"));

const TeacherRegistrationPage = lazy(() => import("../features/teachers/pages/TeacherRegistrationPage"));
const TeacherProfilePage = lazy(() => import("../features/teachers/pages/TeacherProfilePage"));
const TeacherCalendarPage = lazy(() => import("../features/teachers/pages/TeacherCalendarPage"));

const VideoCallRoom = lazy(() => import("../features/sessions/pages/VideoCallRoom"));
const OnboardingPage = lazy(() => import("../features/onboarding/pages/OnboardingPage"));

const TutorialCreator = lazy(() => import("../features/tutorials/components/TutorialCreator"));
const CreateHubPage = lazy(() => import("../features/teachers/pages/CreateHubPage"));
const CreateMultitrackPage = lazy(() => import("../features/teachers/pages/CreateMultitrackPage"));
const AdminApproval = lazy(() => import("../features/admin/components/AdminApproval"));

const PrivacyPage = lazy(() => import("../features/static/pages/PrivacyPage"));
const TermsPage = lazy(() => import("../features/static/pages/TermsPage"));
const PurchaseCompletePage = lazy(() => import("../features/payments/pages/PurchaseCompletePage"));

const AnimatedRoutes = ({ children }) => {
  const location = useLocation();
  return (
    <Routes location={location}>
      {children}
    </Routes>
  );
};

const AppRoutes = () => {
  // COMING SOON MODE - Only show Coming Soon page
  if (COMING_SOON_MODE) {
    return (
      <ErrorBoundary
        title="Application Error"
        description="Something went wrong with the music platform. Please try refreshing the page."
      >
        <Router>
          <Suspense fallback={<div className="min-h-screen w-full bg-[var(--mui-palette-background-default)]" />}>
            <AnimatedRoutes>
              <Route path="/" element={<ComingSoonPage />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </AnimatedRoutes>
          </Suspense>
        </Router>
      </ErrorBoundary>
    );
  }

  // NORMAL MODE - Full platform
  return (
    <ErrorBoundary
      title="Application Error"
      description="Something went wrong with the music platform. Please try refreshing the page."
    >
      <Router>
          <Suspense fallback={<div className="min-h-screen w-full bg-[var(--mui-palette-background-default)]" />}>
            <AnimatedRoutes>
              {/* Public-only routes (redirect to /home if logged in) */}
              <Route element={<PublicRoute redirectPath="/home" />}>
                <Route path="/landing" element={<LandingPage />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/signup" element={<SignupPage />} />
                <Route
                  path="/forgot-password"
                  element={<ForgotPasswordPage />}
                />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
                <Route path="/verify-email" element={<VerifyEmailPage />} />
              </Route>

              {/* Public routes accessible to everyone (but now inside AppLayout to match app feel) */}
              <Route element={<AppLayout><Outlet /></AppLayout>}>
                 <Route path="/learn" element={<LearnPage />} />
                 <Route path="/courses/:id" element={<CourseDetailPage />} />
                 <Route path="/tutorial/:id" element={<TutorialPage />} />
                 <Route path="/multitracks" element={<MultitracksPage />} />
                 <Route path="/multitracks/:id" element={<MultitrackDetailPage />} />
                 {/* Teachers - Profiles */}
                 <Route path="/teachers/:id" element={<TeacherProfilePage />} />
              </Route>

              <Route path="/become-a-teacher" element={<TeacherRegistrationPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/coming-soon" element={<ComingSoonPage />} />

              {/* Protected routes for logged-in users */}
              <Route element={<ProtectedRoute redirectPath="/landing" />}>
                <Route element={<AppLayout><Outlet /></AppLayout>}>
                   <Route path="/" element={<Dashboard />} />
                   <Route path="/home" element={<Dashboard />} />
                   <Route path="/account" element={<AccountPage />} />
                   <Route path="/dashboard" element={<Dashboard />} />
                   <Route path="/feed" element={<FeedPage />} />
                   <Route path="/purchase-complete" element={<PurchaseCompletePage />} />
                   <Route path="/community" element={<Navigate to="/feed" replace />} />
                   <Route path="/profile/:userId" element={<UserProfilePage />} />

                   {/* 1:1 Sessions */}
                   <Route path="/sessions" element={<Navigate to="/home" replace />} />
                </Route>
                <Route
                  path="/sessions/:sessionId/join"
                  element={<VideoCallRoom />}
                />
                <Route path="/onboarding" element={<OnboardingPage />} />
              </Route>

              {/* Teacher & Admin routes */}
              <Route
                element={
                  <ProtectedRoute
                    roles={["teacher", "admin"]}
                    redirectPath="/landing"
                  />
                }
              >
                <Route element={<AppLayout><Outlet /></AppLayout>}>
                  <Route
                    path="/teacher-dashboard"
                    element={<TeacherDashboard />}
                  />
                  <Route path="/dashboard/stripe/success" element={<Navigate to="/teacher-dashboard?stripe=success" replace />} />
                  <Route path="/dashboard/stripe/refresh" element={<Navigate to="/teacher-dashboard?stripe=refresh" replace />} />
                  <Route
                    path="/teacher/create"
                    element={<CreateHubPage />}
                  />
                  <Route
                    path="/teacher/create-course"
                    element={<CourseCreatePage />}
                  />
                  <Route
                    path="/teacher/create-multitrack"
                    element={<StripeConnectGate><CreateMultitrackPage /></StripeConnectGate>}
                  />
                  <Route
                    path="/teacher/create-tutorial"
                    element={<TutorialCreator />}
                  />
                  <Route
                    path="/teacher/edit-course/:id"
                    element={<CourseCreatePage />}
                  />
                  <Route
                    path="/teacher/availability"
                    element={<TeacherCalendarPage />}
                  />
                </Route>
              </Route>

              {/* Admin-only routes */}
              <Route
                element={
                  <ProtectedRoute roles={["admin"]} redirectPath="/landing" />
                }
              >
                <Route element={<AppLayout><Outlet /></AppLayout>}>
                  <Route path="/admin-dashboard" element={<AdminDashboard />} />
                  <Route path="/admin/approval" element={<AdminApproval />} />
                </Route>
              </Route>

              {/* Fallback */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </AnimatedRoutes>

            <EmailVerificationBanner />
            <StripeConnectBanner />
          </Suspense>
          <AchievementNotifications position="top-right" />
        </Router>
    </ErrorBoundary>
  );
};

export default AppRoutes;
